import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import { DataTable, Column } from "../../../DataTable/DataTable";
import { ApiEnvelope } from "../../../../services/api/productCatalog.service";
import { SearchableDropdown } from "../../../../shared/SearchableDropdown";
import { formStyles } from "./catalogConstants";

/**
 * One editable field in a master's create / edit form.
 *
 * `createOnly` fields are hidden when editing, which is how the name field on
 * primary categories, subcategories and groups is handled: the service has no
 * rename for those three, so offering the input would promise an update that
 * never happens.
 */
export interface MasterField {
  name: string;
  label: string;
  type: "text" | "textarea" | "number" | "select" | "boolean";
  options?: { value: string; label: string }[];
  required?: boolean;
  createOnly?: boolean;
  /** Hidden when creating - for a column the create endpoint does not accept. */
  editOnly?: boolean;
  placeholder?: string;
  defaultValue?: unknown;
  helpText?: string;
  /**
   * For a select whose option list is long enough that scrolling it is no use -
   * salts and drugs run into the thousands. The value is typed and matched
   * instead of picked.
   */
  searchable?: boolean;
}

interface MasterPanelProps<T extends { id: string | number }> {
  title: string;
  /** One line under the title explaining what the master is for. */
  description?: string;
  addLabel: string;
  columns: Column<T>[];
  fields: MasterField[];
  fetchRows: (args: {
    page: number;
    pageSize: number;
    search: string;
  }) => Promise<ApiEnvelope<T[]>>;
  onCreate: (values: Record<string, any>) => Promise<ApiEnvelope>;
  onUpdate?: (row: T, values: Record<string, any>) => Promise<ApiEnvelope>;
  /** Maps an existing row onto form values when the edit dialog opens. */
  rowToValues?: (row: T) => Record<string, any>;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Extra filter controls shown in the header, left of the search box. */
  filters?: React.ReactNode;
  /** Changing this re-runs the fetch - pass any filter state the panel does not own. */
  refreshToken?: string | number;
  /**
   * Fires whenever the dialog's values change, so a parent can cascade: react
   * to one field and hand back a `fields` list with narrowed options.
   */
  onValuesChange?: (values: Record<string, any>) => void;
  /** Rendered above the table, e.g. the cascading pickers of a mapping form. */
  children?: React.ReactNode;
}

/**
 * Listing + create + edit for one catalogue master.
 *
 * Every master behind this screen shares the same contract - a paginated list,
 * a create, and an update that at minimum toggles `isActive` - so they share
 * one panel driven by a field list instead of ten near-identical components.
 */
export function MasterPanel<T extends { id: string | number }>({
  title,
  description,
  addLabel,
  columns,
  fields,
  fetchRows,
  onCreate,
  onUpdate,
  rowToValues,
  searchable = true,
  searchPlaceholder = "Search...",
  filters,
  refreshToken,
  onValuesChange,
  children,
}: MasterPanelProps<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [dialogRow, setDialogRow] = useState<T | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Held in a ref so an inline callback does not re-fire the notification.
  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;
  useEffect(() => {
    if (isDialogOpen) onValuesChangeRef.current?.(values);
  }, [values, isDialogOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, refreshToken]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchRows({
        page: currentPage,
        pageSize,
        search: debouncedSearch,
      });
      if (res?.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalPages(res.totalPages ?? 1);
      } else {
        setRows([]);
        setTotalPages(1);
        if (res?.message) toast.error(res.message);
      }
    } catch (error) {
      toast.error(`${error}`);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchRows, currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    load();
    // `load` already closes over every input it reads.
  }, [load, refreshToken]);

  const defaultValues = useMemo(() => {
    const next: Record<string, any> = {};
    fields.forEach((field) => {
      next[field.name] =
        field.defaultValue !== undefined
          ? field.defaultValue
          : field.type === "boolean"
            ? true
            : field.type === "number"
              ? ""
              : "";
    });
    return next;
  }, [fields]);

  const openCreate = () => {
    setDialogRow(null);
    setValues(defaultValues);
    setIsDialogOpen(true);
  };

  const openEdit = (row: T) => {
    setDialogRow(row);
    setValues({ ...defaultValues, ...(rowToValues ? rowToValues(row) : {}) });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setDialogRow(null);
  };

  const visibleFields = fields.filter((field) =>
    dialogRow ? !field.createOnly : !field.editOnly
  );

  const handleSave = async () => {
    const missing = visibleFields.find(
      (field) =>
        field.required &&
        (values[field.name] === "" ||
          values[field.name] === undefined ||
          values[field.name] === null)
    );
    if (missing) {
      toast.error(`${missing.label} is required`);
      return;
    }

    setIsSaving(true);
    try {
      const payload: Record<string, any> = {};
      visibleFields.forEach((field) => {
        const value = values[field.name];
        if (value === "" || value === undefined) return;
        payload[field.name] = field.type === "number" ? Number(value) : value;
      });

      const res =
        dialogRow && onUpdate
          ? await onUpdate(dialogRow, payload)
          : await onCreate(payload);

      if (res?.success) {
        toast.success(res.message || (dialogRow ? "Updated" : "Created"));
        closeDialog();
        load();
      } else {
        toast.error(res?.message || "Save failed");
      }
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      setIsSaving(false);
    }
  };

  /** Appended so every master gets a consistent Edit affordance. */
  const tableColumns: Column<T>[] = onUpdate
    ? [
        ...columns,
        {
          key: "__actions",
          title: "Actions",
          width: "w-[110px]",
          render: (row: T) => (
            <button
              type="button"
              onClick={() => openEdit(row)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Edit
            </button>
          ),
        },
      ]
    : columns;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          {description && (
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {filters}
          {searchable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-56 pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          )}
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            {addLabel}
          </button>
        </div>
      </div>

      {children}

      <DataTable
        data={rows}
        columns={tableColumns}
        isLoading={isLoading}
        itemsPerPage={pageSize}
        customFilters={null}
        filterComponent={null}
        pagination={{
          currentPage,
          setCurrentPage,
          totalPages,
          setTotalPages,
          pageSize,
          setPageSize,
        }}
      />

      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-800">
                {dialogRow ? `Edit ${title}` : addLabel}
              </h3>
              <button
                type="button"
                onClick={closeDialog}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              {visibleFields.map((field) => (
                <div key={field.name}>
                  <label className={formStyles.lableStyle}>
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-0.5">*</span>
                    )}
                  </label>

                  {field.type === "boolean" ? (
                    <div className="flex gap-4 mt-1">
                      {[true, false].map((option) => (
                        <label
                          key={String(option)}
                          className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={field.name}
                            checked={values[field.name] === option}
                            onChange={() =>
                              setValues((prev) => ({
                                ...prev,
                                [field.name]: option,
                              }))
                            }
                            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          {option ? "Yes" : "No"}
                        </label>
                      ))}
                    </div>
                  ) : field.type === "select" && field.searchable ? (
                    <SearchableValueInput
                      value={values[field.name] ?? ""}
                      options={field.options || []}
                      placeholder={
                        field.placeholder ||
                        `Type a ${field.label.toLowerCase()} name`
                      }
                      onChange={(value) =>
                        setValues((prev) => ({ ...prev, [field.name]: value }))
                      }
                    />
                  ) : field.type === "select" ? (
                    <select
                      value={values[field.name] ?? ""}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      className={formStyles.inputStyle}
                    >
                      <option value="">Select {field.label}</option>
                      {(field.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea
                      value={values[field.name] ?? ""}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      placeholder={field.placeholder}
                      className={`${formStyles.inputStyle} h-24 resize-none`}
                    />
                  ) : (
                    <input
                      type={field.type === "number" ? "number" : "text"}
                      value={values[field.name] ?? ""}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      placeholder={field.placeholder}
                      className={formStyles.inputStyle}
                    />
                  )}

                  {field.helpText && (
                    <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                type="button"
                onClick={closeDialog}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {isSaving ? "Saving..." : dialogRow ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Green / grey pill used for the `isActive` and `isAllowed` columns. */
export function StatusPill({
  value,
  trueLabel = "Active",
  falseLabel = "Inactive",
}: {
  value: boolean | null | undefined;
  trueLabel?: string;
  falseLabel?: string;
}) {
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
        value
          ? "bg-emerald-50 text-emerald-700"
          : "bg-gray-100 text-gray-500"
      }`}
    >
      {value ? trueLabel : falseLabel}
    </span>
  );
}

/** Small labelled select used by the panels' header filters. */
/**
 * A text box that resolves to one of `options`.
 *
 * Shared by the dialog's searchable selects and by FilterSearchSelect, so both
 * behave the same: typing filters, picking fills the box, emptying the box
 * clears the value.
 */
function SearchableValueInput({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const [searchText, setSearchText] = useState("");
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLabel =
    options.find((option) => option.value === value)?.label || "";

  // Shows the stored value's label once the options arrive, leaving whatever is
  // being typed alone.
  useEffect(() => {
    if (selectedLabel && !searchText) setSearchText(selectedLabel);
  }, [selectedLabel, searchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSuggestionsVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <SearchableDropdown
        placeholder={placeholder}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
          if (!text.trim()) onChange("");
        }}
        suggestionsVisible={suggestionsVisible}
        setSuggestionsVisible={setSuggestionsVisible}
        options={options
          .filter((option) => option.value !== "")
          .map((option) => ({ id: option.value, label: option.label }))}
        onSelect={(selected) => {
          onChange(selected.id);
          setSearchText(selected.label);
        }}
        dropdownRef={dropdownRef}
      />
      {/* Typing something that matches nothing leaves the picked value in
          force, so say which one that is rather than let the box mislead. */}
      {value && selectedLabel && selectedLabel !== searchText.trim() && (
        <p className="text-xs text-emerald-600 mt-1">Using: {selectedLabel}</p>
      )}
    </>
  );
}

/** FilterSelect for a list too long to scroll: the value is typed. */
export function FilterSearchSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  width = "w-56",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  width?: string;
}) {
  return (
    <div className={width}>
      <label className="block text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">
        {label}
      </label>
      <SearchableValueInput
        value={value}
        options={options}
        placeholder={placeholder || `Type a ${label.toLowerCase()} name`}
        onChange={onChange}
      />
    </div>
  );
}

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  width = "w-44",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  width?: string;
}) {
  return (
    <div className={width}>
      <label className="block text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
